import MailIcon from '../../assets/icons/mail.svg?react';
import PlusIcon from '../../assets/icons/plus.svg?react';
import TeamsIcon from '../../assets/icons/teams.svg?react';
import {
  Alarm,
  ChannelType,
  useGetAlertGroupsQuery,
  useGetNotificationChannelsQuery,
} from '../../gql/generated';
import { Badge } from '../Badge';

interface AlarmChannelsTabProps {
  alarm: Alarm;
}

function ChannelTypeIcon({ type }: { type: ChannelType }) {
  switch (type) {
    case ChannelType.Teams:
      return <TeamsIcon className='text-primary h-4 w-4' />;
    case ChannelType.Email:
      return <MailIcon className='text-primary h-4 w-4' />;
    case ChannelType.Grouped:
      return <PlusIcon className='text-primary h-4 w-4' />;
  }
}

const channelTypeLabels: Record<ChannelType, string> = {
  [ChannelType.Email]: 'Email',
  [ChannelType.Teams]: 'Teams',
  [ChannelType.Grouped]: 'Grouped',
};

export function AlarmChannelsTab({ alarm }: AlarmChannelsTabProps) {
  const alertGroups = alarm.alertGroups || [];

  // An alarm keeps no channels of its own. Its alert group holds them and sends
  // one message for every signal in it, so this tab reads the group: EDMS
  // evaluates and notifies groups only.
  const { data: groupData, loading: groupsLoading } = useGetAlertGroupsQuery({
    variables: { pagination: { offset: 0, limit: 1000 } },
    skip: alertGroups.length === 0,
  });

  const groupIds = alertGroups.map((group) => group.id);
  const owningGroups =
    groupData?.alertGroups.rows.filter((group) =>
      groupIds.includes(group.id)
    ) || [];
  const groupChannelIds = owningGroups.flatMap(
    (group) => group.notificationChannelIds || []
  );

  const channelIds = Array.from(new Set(groupChannelIds));
  const deliversThroughGroup = channelIds.length > 0;

  const { data, loading, error } = useGetNotificationChannelsQuery({
    variables: {
      pagination: { offset: 0, limit: 1000 },
    },
    skip: channelIds.length === 0,
  });

  const linkedChannels =
    data?.notificationChannels.rows.filter((channel) =>
      channelIds.includes(channel.id)
    ) || [];

  if (loading || groupsLoading) {
    return (
      <div className='flex flex-col items-center justify-center py-12'>
        <p className='size-p2 text-tertiary'>Loading channels...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex flex-col items-center justify-center py-12'>
        <p className='size-p2 text-error'>Failed to load channels</p>
      </div>
    );
  }

  if (channelIds.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-12'>
        <p className='size-p2 text-tertiary'>
          No notification channels linked to this Pulse alert
        </p>
      </div>
    );
  }

  return (
    <div className='flex flex-col'>
      {deliversThroughGroup && (
        <p className='size-p2 text-secondary px-4 py-3'>
          Delivered through {owningGroups.map((group) => group.name).join(', ')}
          . One notification covers every signal in the group.
        </p>
      )}
      <table className='w-full border-collapse'>
        {/* Table header */}
        <thead>
          <tr
            className='size-p2 text-secondary border-border-secondary
              bg-neutral-050 border-y'
          >
            <th className='py-4 pr-4 pl-4 text-left font-normal'>Name</th>
            <th className='px-4 py-4 text-left font-normal'>Type</th>
            <th className='w-32 py-4 pr-4 pl-4 text-left font-normal'>
              Status
            </th>
          </tr>
        </thead>

        {/* Table body */}
        <tbody>
          {linkedChannels.map((channel) => (
            <tr
              key={channel.id}
              className='border-b border-gray-100 bg-white transition-colors'
            >
              <td className='size-p2 text-primary py-3 pr-4 pl-4'>
                <div className='flex items-center gap-2'>
                  <ChannelTypeIcon type={channel.channelType} />
                  <span>{channel.name}</span>
                </div>
              </td>
              <td className='size-p2 text-primary px-4 py-3'>
                {channelTypeLabels[channel.channelType]}
              </td>
              <td className='py-3 pr-4 pl-4'>
                <Badge variant={channel.isEnabled ? 'success' : 'neutral'}>
                  {channel.isEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Empty state */}
      {linkedChannels.length === 0 && (
        <div className='flex flex-col items-center justify-center py-12'>
          <p className='size-p2 text-tertiary'>
            No notification channels linked
          </p>
        </div>
      )}
    </div>
  );
}
